import { NoSsr } from '@mui/material';
import type {
  HeadlineResponse,
  HeadlineResponsePagedData,
  LatestForumPostsResponse,
  MenuResponse,
  NilHundredResponseNil100FilterResponseRelatedPagedData,
  PlayerItemResponsePagedData,
  ScoreboardResponseRelatedScheduleResponseRelatedPagedData,
  TrendingListItemResponseTrendingListResponseRelatedPagedData,
} from '@on3/api';
import { useSite } from '@on3/ui-lib/src/contexts/SiteProvider';
import { MobileMPU } from '@public/components/Ads/MobileMPU';
import { MPU } from '@public/components/Ads/MPU';
import { PostFeed } from '@public/components/Forums/Forums';
import { CoverList } from '@public/components/Headlines/ArticleCover/CoverList';
import { FeedList } from '@public/components/Headlines/ArticleFeed/FeedList';
import { TopHeadlinesList } from '@public/components/Headlines/TopHeadlines/TopHeadlinesList';
import { SEOHomeText } from '@public/components/Home/SEOHomeText';
import { HomeLayout } from '@public/components/Layouts/HomeLayout';
import { SignUpFooter } from '@public/components/Newsletter/SignUpFooter';
import { Nil100Widget } from '@public/components/Nil/Nil100Widget';
import { PianoPromo } from '@public/components/Promos/PianoPromo';
import { ScheduleModule } from '@public/components/Schedules/Sidebar/Module';
import { TeamConnect } from '@public/components/Social/TeamConnect';
import { LatestCommits } from '@public/components/Teams/LatestCommits';
import { QuickLinks } from '@public/components/Teams/QuickLinks';
import { TopHeadlines } from '@public/components/TopHeadlines';
import { getArticleLists } from '@public/utils/articles';
import { createTitle } from '@public/utils/meta';

import styles from './TraditionalHomeExtended.module.scss';

export interface ITraditionalHomeExtendedProps {
  featuredArticles: HeadlineResponse[] | null;
  feedArticles: HeadlineResponsePagedData | null;
  channelSportsArticles: HeadlineResponse[] | null;
  channelRecruitingArticles: HeadlineResponse[] | null;
  messageBoard: LatestForumPostsResponse | null;
  latestCommits: PlayerItemResponsePagedData | null;
  quickLinks: MenuResponse[] | null;
  teamNil100: NilHundredResponseNil100FilterResponseRelatedPagedData | null;
  schedule: ScoreboardResponseRelatedScheduleResponseRelatedPagedData | null;
  trendingList:
    | TrendingListItemResponseTrendingListResponseRelatedPagedData[]
    | TrendingListItemResponseTrendingListResponseRelatedPagedData
    | null;
}

export const TraditionalHomeExtended = ({
  featuredArticles,
  feedArticles,
  channelSportsArticles,
  channelRecruitingArticles,
  messageBoard,
  latestCommits,
  quickLinks,
  teamNil100,
  schedule,
  trendingList,
}: ITraditionalHomeExtendedProps) => {
  const { currentSite, siblingSite } = useSite();
  const { siteName } = currentSite || {};
  const siteColor = currentSite?.primaryColor || '#fe3b1f';

  const [
    featuredArticlesList,
    feedArticlesList,
    channelSportsArticlesList,
    channelRecruitingArticlesList,
  ] = getArticleLists({
    featuredArticles: {
      list: featuredArticles ?? [],
      sliceLimit: 3,
    },
    lists: [
      {
        list: feedArticles?.list ?? [],
        sliceLimit: 15,
        removeDuplicates: true,
      },
      {
        list: channelSportsArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: channelRecruitingArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
    ],
  });

  const feedArticleLinks = [
    {
      url: `${currentSite?.url}/news/`,
      title: createTitle(siteName, 'News'),
      text: 'View All',
    },
  ];

  const forumViewAll = messageBoard?.primaryForum?.url
    ? [
        {
          url: messageBoard.primaryForum.url,
          text: 'View All',
          title: 'View All Message Board Posts',
        },
      ]
    : [];

  const topHeadlinesHeading = createTitle(
    'Top',
    currentSite?.isChannel
      ? currentSite.siteName
      : currentSite?.organization?.knownAs || currentSite?.organization?.name,
    'Headlines',
  );

  const topHeadlinesLinks = [
    {
      title: 'View All',
      href: `${siblingSite?.url}/news/`,
    },
    {
      title: createTitle(
        siblingSite?.organization?.knownAs || siblingSite?.organization?.name,
        'Channel',
      ),
      href: `${siblingSite?.url}/`,
    },
  ];

  return (
    <>
      {!!featuredArticlesList && (
        <CoverList
          authorColor="var(--header-text-color, #fff)"
          backgroundColor={siteColor}
          categoryColor="var(--header-text-color, #fff)"
          list={featuredArticlesList.slice(0, 3)}
          titleColor="var(--header-text-color, #fff)"
        >
          <NoSsr>
            <PianoPromo id="pianoPromoDark" />
          </NoSsr>
        </CoverList>
      )}
      <SEOHomeText />
      <HomeLayout>
        <section className={styles.feed}>
          <div className={styles.inner}>
            {!!feedArticlesList.length && (
              <div data-section="news">
                <FeedList
                  list={feedArticlesList}
                  section={createTitle(siteName, 'Headlines')}
                  viewAllLinks={feedArticleLinks}
                />
              </div>
            )}

            <MobileMPU id={'mobile_mpu_top'} incontent />

            {!!messageBoard?.posts?.length && (
              <div data-section="boards">
                <PostFeed
                  forum={messageBoard?.primaryForum}
                  links={forumViewAll}
                  posts={messageBoard?.posts}
                  title="Popular Message Board Posts"
                />
              </div>
            )}

            {(!!channelSportsArticlesList?.length ||
              !!channelRecruitingArticlesList?.length) && (
              <div data-section="headlines">
                <TopHeadlinesList
                  heading={topHeadlinesHeading}
                  links={topHeadlinesLinks}
                  list={[
                    ...(channelSportsArticlesList || []),
                    ...(channelRecruitingArticlesList || []),
                  ].slice(0, 5)}
                />
              </div>
            )}
          </div>
        </section>
        <aside className={styles.sidebarleft}>
          <div className={styles.inner}>
            {latestCommits?.list?.length && (
              <LatestCommits list={latestCommits?.list} />
            )}

            {!!schedule?.list?.length && (
              <ScheduleModule
                list={schedule?.list}
                relatedModel={schedule?.relatedModel}
              />
            )}

            <TeamConnect />
          </div>
        </aside>
        <aside className={styles.sidebarright}>
          <div className={styles.inner}>
            <MPU id={'mpu_top'} />

            {!!teamNil100?.list?.length && (
              <Nil100Widget isModule list={teamNil100?.list} />
            )}

            {!!quickLinks?.length && (
              <QuickLinks quickLinks={quickLinks} title="Quick Links" />
            )}

            {!!trendingList && <TopHeadlines trendingList={trendingList} />}

            <MPU id={'mpu_middle'} />
            <MobileMPU id={'mobile_mpu_middle'} incontent />
          </div>
        </aside>
      </HomeLayout>
      <SignUpFooter titleText={`${siteName} Newsletter`} />
    </>
  );
};
